import { generateId, logger } from "@openzeppelin/ui-utils";
import Dexie from "dexie";
import { useLiveQuery, useLiveQuery as useLiveQuery$1 } from "dexie-react-hooks";
import { useCallback, useMemo, useRef } from "react";

//#region src/base/utils.ts
/**
* Shared utilities for storage classes.
*/
/**
* Checks if an error is a quota exceeded error.
* Handles cross-browser differences in error reporting.
*
* @param err - The error to check
* @returns true if the error indicates storage quota was exceeded
*/
function isQuotaError(err) {
	if (!err || typeof err !== "object") return false;
	const e = err;
	return e?.name === "QuotaExceededError" || e?.code === 22 || typeof e?.message === "string" && e.message.toLowerCase().includes("quota");
}
/**
* Wraps an async operation with quota error handling.
* Converts quota errors to a standardized error format with the original as cause.
*
* @param tableName - The table name for error message context
* @param operation - The async operation to execute
* @returns The result of the operation
* @throws Error with message `${tableName}/quota-exceeded` if quota is exceeded
*/
async function withQuotaHandling(tableName, operation) {
	try {
		return await operation();
	} catch (err) {
		if (isQuotaError(err)) {
			const e = /* @__PURE__ */ new Error(`${tableName}/quota-exceeded`);
			e.cause = err;
			throw e;
		}
		throw err;
	}
}

//#endregion
//#region src/base/EntityStorage.ts
const DEFAULT_OPTIONS$2 = { maxRecordSizeBytes: 10 * 1024 * 1024 };
/**
* Abstract base class for entity storage using IndexedDB via Dexie.
*
* Use this class for storing collections of entities/records where each
* record has an auto-generated `id` and managed timestamps.
*
* For key-value storage (settings, preferences), use `KeyValueStorage` instead.
*
* @example
* ```typescript
* import { EntityStorage, createDexieDatabase } from '@openzeppelin/ui-storage';
*
* interface UserRecord extends BaseRecord {
*   name: string;
*   email: string;
* }
*
* const db = createDexieDatabase('MyApp', [{
*   version: 1,
*   stores: { users: '++id, email, createdAt, updatedAt' }
* }]);
*
* class UserStorage extends EntityStorage<UserRecord> {
*   constructor() {
*     super(db, 'users');
*   }
*
*   async findByEmail(email: string): Promise<UserRecord | undefined> {
*     const results = await this.findByIndex('email', email);
*     return results[0];
*   }
* }
* ```
*
* @typeParam T - The record type (must extend BaseRecord)
*/
var EntityStorage = class {
	/**
	* Creates a new EntityStorage instance.
	* @param db - Dexie database instance
	* @param tableName - Name of the table to use
	* @param options - Optional storage configuration
	*/
	constructor(db, tableName, options) {
		this.db = db;
		this.tableName = tableName;
		this.table = db.table(tableName);
		this.options = {
			...DEFAULT_OPTIONS$2,
			...options
		};
		this.table.hook("creating", function(_primKey, obj, _trans) {
			obj.id = obj.id || generateId();
			obj.createdAt = /* @__PURE__ */ new Date();
			obj.updatedAt = /* @__PURE__ */ new Date();
		});
		this.table.hook("updating", function(modifications, _primKey, _obj, _trans) {
			return {
				...modifications,
				updatedAt: /* @__PURE__ */ new Date()
			};
		});
	}
	/**
	* Validates a record before storage.
	* Override in subclasses for custom validation.
	*
	* @param record - The record to validate
	* @throws Error if validation fails
	*/
	validateRecord(record) {
		try {
			if (JSON.stringify(record).length > this.options.maxRecordSizeBytes) throw new Error(`${this.tableName}/record-too-large`);
		} catch (err) {
			if (err instanceof Error && err.message.includes("record-too-large")) throw err;
			throw new Error(`${this.tableName}/record-not-serializable`);
		}
	}
	/**
	* Saves a new record to storage.
	*
	* @param record - The record data (without id, createdAt, updatedAt)
	* @returns The generated ID of the saved record
	* @throws Error if validation fails or quota is exceeded
	*/
	async save(record) {
		this.validateRecord(record);
		return await withQuotaHandling(this.tableName, async () => {
			const id = generateId();
			const fullRecord = {
				...record,
				id,
				createdAt: /* @__PURE__ */ new Date(),
				updatedAt: /* @__PURE__ */ new Date()
			};
			await this.table.add(fullRecord);
			logger.info(`${this.tableName} saved`, `ID: ${id}`);
			return id;
		});
	}
	/**
	* Updates an existing record.
	*
	* @param id - The record ID to update
	* @param updates - Partial record with fields to update
	* @throws Error if record not found, validation fails, or quota is exceeded
	*/
	async update(id, updates) {
		return await withQuotaHandling(this.tableName, async () => {
			const existing = await this.table.get(id);
			if (!existing) throw new Error(`No record found with id: ${id}`);
			const { id: _, createdAt: _createdAt, ...validUpdates } = updates;
			const { id: _existingId, createdAt: _existingCreatedAt, updatedAt: _existingUpdatedAt, ...existingData } = existing;
			const mergedData = {
				...existingData,
				...validUpdates
			};
			this.validateRecord(mergedData);
			if (await this.table.update(id, validUpdates) === 0) throw new Error(`No record found with id: ${id}`);
			logger.info(`${this.tableName} updated`, `ID: ${id}`);
		});
	}
	/**
	* Deletes a record by ID.
	*
	* @param id - The record ID to delete
	*/
	async delete(id) {
		await this.table.delete(id);
		logger.info(`${this.tableName} deleted`, `ID: ${id}`);
	}
	/**
	* Gets a record by ID.
	*
	* @param id - The record ID to retrieve
	* @returns The record, or undefined if not found
	*/
	async get(id) {
		return await this.table.get(id);
	}
	/**
	* Gets all records, ordered by updatedAt descending (most recent first).
	*
	* @returns Array of all records
	*/
	async getAll() {
		return await this.table.orderBy("updatedAt").reverse().toArray();
	}
	/**
	* Clears all records from the table.
	*/
	async clear() {
		await this.table.clear();
		logger.info(`${this.tableName} storage cleared`, "All records removed");
	}
	/**
	* Checks if a record exists by ID.
	*
	* @param id - The record ID to check
	* @returns true if the record exists
	*/
	async has(id) {
		return await this.table.get(id) !== void 0;
	}
	/**
	* Gets the count of records in the table.
	*
	* @returns The number of records
	*/
	async count() {
		return await this.table.count();
	}
	/**
	* Bulk add records to the table.
	* Note: Bulk operations do not trigger 'creating' hooks, so timestamps must be set manually.
	*
	* @param records - Array of complete records to add
	* @returns Array of generated IDs
	* @throws Error if any record exceeds size limit or quota is exceeded
	*/
	async bulkAdd(records) {
		for (const record of records) {
			const { id: _, createdAt: _createdAt, updatedAt: _updatedAt, ...recordData } = record;
			this.validateRecord(recordData);
		}
		return await withQuotaHandling(this.tableName, async () => {
			const keys = await this.table.bulkAdd(records, { allKeys: true });
			logger.info(`${this.tableName} bulk add`, `Count: ${keys.length}`);
			return keys;
		});
	}
	/**
	* Bulk put (upsert) records to the table.
	* Note: Bulk operations do not trigger hooks, so timestamps must be set manually.
	*
	* @param records - Array of complete records to upsert
	* @throws Error if any record exceeds size limit or quota is exceeded
	*/
	async bulkPut(records) {
		for (const record of records) {
			const { id: _, createdAt: _createdAt, updatedAt: _updatedAt, ...recordData } = record;
			this.validateRecord(recordData);
		}
		return await withQuotaHandling(this.tableName, async () => {
			await this.table.bulkPut(records);
			logger.info(`${this.tableName} bulk put`, `Count: ${records.length}`);
		});
	}
	/**
	* Bulk delete records by IDs.
	*
	* @param ids - Array of record IDs to delete
	*/
	async bulkDelete(ids) {
		await this.table.bulkDelete(ids);
		logger.info(`${this.tableName} bulk delete`, `Count: ${ids.length}`);
	}
	/**
	* Find records by an indexed field.
	*
	* @param index - The index name (must be defined in the Dexie schema)
	* @param value - The value to search for
	* @returns Array of matching records
	*/
	async findByIndex(index, value) {
		const results = await this.table.where(index).equals(value).toArray();
		logger.info(`${this.tableName} findByIndex`, `Index: ${index}, Count: ${results.length}`);
		return results;
	}
};

//#endregion
//#region src/base/KeyValueStorage.ts
const DEFAULT_OPTIONS$1 = {
	maxKeyLength: 128,
	maxValueSizeBytes: 1024 * 1024
};
/**
* Abstract base class for key-value storage using IndexedDB via Dexie.
*
* Unlike `EntityStorage<T>` which uses auto-generated `id` fields,
* this class is designed for key-value stores where the `key` field
* is the primary key (schema: `&key`).
*
* @example
* ```typescript
* // Define your database
* const db = createDexieDatabase('MyApp', [{
*   version: 1,
*   stores: { settings: '&key' }
* }]);
*
* // Create a storage class
* class SettingsStorage extends KeyValueStorage<string> {
*   constructor() {
*     super(db, 'settings');
*   }
* }
*
* // Use it
* const settings = new SettingsStorage();
* await settings.set('theme', 'dark');
* const theme = await settings.get('theme'); // 'dark'
* ```
*
* @typeParam V - The type of values stored (default: unknown)
*/
var KeyValueStorage = class {
	/**
	* Creates a new KeyValueStorage instance.
	* @param db - Dexie database instance
	* @param tableName - Name of the table to use
	* @param options - Optional storage configuration
	*/
	constructor(db, tableName, options) {
		this.db = db;
		this.tableName = tableName;
		this.table = db.table(tableName);
		this.options = {
			...DEFAULT_OPTIONS$1,
			...options
		};
	}
	/**
	* Validates and normalizes a key.
	* @throws Error if key is invalid
	*/
	validateKey(key) {
		if (typeof key !== "string") throw new Error(`${this.tableName}/invalid-key`);
		const trimmed = key.trim();
		if (trimmed.length === 0) throw new Error(`${this.tableName}/invalid-key`);
		if (trimmed.length > this.options.maxKeyLength) throw new Error(`${this.tableName}/key-too-long`);
		return trimmed;
	}
	/**
	* Validates a value before storage.
	* @throws Error if value is invalid or too large
	*/
	validateValue(value) {
		if (value === void 0) throw new Error(`${this.tableName}/invalid-value`);
		if (typeof value === "string") {
			if (value.length > this.options.maxValueSizeBytes) throw new Error(`${this.tableName}/value-too-large`);
			return;
		}
		if (value !== null && typeof value === "object") try {
			if (JSON.stringify(value).length > this.options.maxValueSizeBytes) throw new Error(`${this.tableName}/value-too-large`);
		} catch (err) {
			if (err instanceof Error && err.message.includes("value-too-large")) throw err;
			throw new Error(`${this.tableName}/value-not-serializable`);
		}
	}
	/**
	* Sets a value for a key. Creates the entry if it doesn't exist,
	* or updates it if it does (upsert semantics).
	*
	* @param key - The key (will be trimmed)
	* @param value - The value to store
	* @throws Error if key or value is invalid, or quota is exceeded
	*/
	async set(key, value) {
		const normalizedKey = this.validateKey(key);
		this.validateValue(value);
		await withQuotaHandling(this.tableName, async () => {
			const now = /* @__PURE__ */ new Date();
			const record = {
				key: normalizedKey,
				value,
				createdAt: (await this.table.get(normalizedKey))?.createdAt ?? now,
				updatedAt: now
			};
			await this.table.put(record);
			logger.info(`${this.tableName} set`, `Key: ${normalizedKey}`);
		});
	}
	/** Implementation of get method overloads */
	async get(key) {
		const normalizedKey = this.validateKey(key);
		return (await this.table.get(normalizedKey))?.value;
	}
	/**
	* Gets a value with a default fallback.
	*
	* @param key - The key to look up
	* @param defaultValue - Value to return if key doesn't exist
	* @returns The stored value or the default
	*/
	async getOrDefault(key, defaultValue) {
		const value = await this.get(key);
		return value !== void 0 ? value : defaultValue;
	}
	/**
	* Deletes a key-value pair.
	*
	* @param key - The key to delete
	*/
	async delete(key) {
		const normalizedKey = this.validateKey(key);
		await this.table.delete(normalizedKey);
		logger.info(`${this.tableName} deleted`, `Key: ${normalizedKey}`);
	}
	/**
	* Checks if a key exists.
	*
	* @param key - The key to check
	* @returns true if the key exists
	*/
	async has(key) {
		const normalizedKey = this.validateKey(key);
		return await this.table.get(normalizedKey) !== void 0;
	}
	/**
	* Gets all keys in the store.
	*
	* @returns Array of all keys
	*/
	async keys() {
		return (await this.table.toArray()).map((r) => r.key);
	}
	/**
	* Gets all key-value pairs.
	*
	* @returns Array of all records
	*/
	async getAll() {
		return await this.table.toArray();
	}
	/**
	* Clears all key-value pairs.
	*/
	async clear() {
		await this.table.clear();
		logger.info(`${this.tableName} cleared`, "All entries removed");
	}
	/**
	* Gets the number of entries in the store.
	*
	* @returns The count of stored key-value pairs
	*/
	async count() {
		return await this.table.count();
	}
	/**
	* Sets multiple key-value pairs at once.
	* All entries are validated before writing. If validation fails, no entries are written.
	* Note: The bulkPut operation itself may be non-atomic if quota or database errors occur.
	*
	* @param entries - Object or Map of key-value pairs
	* @throws Error if any key or value validation fails
	*/
	async setMany(entries) {
		const items = entries instanceof Map ? Array.from(entries.entries()) : Object.entries(entries);
		const normalizedKeys = items.map(([key]) => this.validateKey(key));
		items.forEach(([, value]) => this.validateValue(value));
		const existingRecords = await this.table.bulkGet(normalizedKeys);
		const existingMap = new Map(existingRecords.filter((r) => r !== void 0).map((r) => [r.key, r]));
		const now = /* @__PURE__ */ new Date();
		const records = items.map(([, value], index) => {
			const normalizedKey = normalizedKeys[index];
			return {
				key: normalizedKey,
				value,
				createdAt: existingMap.get(normalizedKey)?.createdAt ?? now,
				updatedAt: now
			};
		});
		await withQuotaHandling(this.tableName, async () => {
			await this.table.bulkPut(records);
			logger.info(`${this.tableName} setMany`, `Count: ${records.length}`);
		});
	}
	/**
	* Gets multiple values by keys.
	*
	* @param keys - Array of keys to look up
	* @returns Map of key to value (missing keys are omitted)
	*/
	async getMany(keys) {
		const normalizedKeys = keys.map((k) => this.validateKey(k));
		const records = await this.table.bulkGet(normalizedKeys);
		const result = /* @__PURE__ */ new Map();
		for (const record of records) if (record) result.set(record.key, record.value);
		return result;
	}
	/**
	* Deletes multiple keys at once.
	*
	* @param keys - Array of keys to delete
	*/
	async deleteMany(keys) {
		const normalizedKeys = keys.map((k) => this.validateKey(k));
		await this.table.bulkDelete(normalizedKeys);
		logger.info(`${this.tableName} deleteMany`, `Count: ${normalizedKeys.length}`);
	}
};

//#endregion
//#region src/core/db.ts
/**
* Creates a configured Dexie database instance with versioned stores.
*
* @param name - Database name (e.g., 'UIBuilder', 'RoleManager')
* @param versions - Array of version definitions with stores and optional upgrade functions
* @returns Configured Dexie database instance
*
* @example
* ```ts
* const db = createDexieDatabase('MyApp', [
*   {
*     version: 1,
*     stores: { items: '++id, name, createdAt' }
*   },
*   {
*     version: 2,
*     stores: { items: '++id, name, createdAt, updatedAt' },
*     upgrade: async (trans) => {
*       // Migration logic - access db via trans.db if needed
*     }
*   }
* ]);
* ```
*/
function createDexieDatabase(name, versions) {
	const db = new Dexie(name);
	for (const version of versions) {
		const versionDef = db.version(version.version).stores(version.stores);
		if (version.upgrade) versionDef.upgrade(version.upgrade);
	}
	return db;
}

//#endregion
//#region src/react/createLiveQueryHook.ts
/**
* Creates a React hook for live queries on a Dexie table that automatically updates when data changes.
*
* @template T The type of records in the table
* @param db The Dexie database instance
* @param tableName The name of the table to query
* @param query Optional custom query function. If omitted, returns all records from the table.
* @returns A hook that returns the query results or undefined while loading
*/
function createLiveQueryHook(db, tableName, query) {
	return function useTable() {
		return useLiveQuery$1(() => {
			const table = db.table(tableName);
			if (query) return query(table);
			return table.toArray();
		});
	};
}

//#endregion
//#region src/react/createCrudHook.ts
/**
* Creates a React hook that wraps CRUD repository operations with error handling.
*
* @template T The type of records managed by the repository
* @param repo The CRUD repository to wrap
* @param opts Optional configuration
* @param opts.onError Optional error handler called with a title and the error
* @returns A hook that provides wrapped CRUD operations (save, update, remove, clear)
*/
function createCrudHook(repo, opts) {
	const onError = opts?.onError;
	const wrap = async (title, action) => {
		try {
			return await action();
		} catch (error) {
			onError?.(title, error);
			throw error;
		}
	};
	return function useCrud() {
		return {
			save: (record) => wrap("Failed to save", () => repo.save(record)),
			update: (id, updates) => wrap("Failed to update", () => repo.update(id, updates)),
			remove: (id) => wrap("Failed to delete", () => repo.delete(id)),
			clear: () => wrap("Failed to delete all", () => repo.clear())
		};
	};
}

//#endregion
//#region src/react/createJsonFileIO.ts
/**
* Creates file I/O utilities for importing and exporting data as JSON files with error handling.
*
* @param functions Object containing export and import functions
* @param functions.exportJson Function that exports data to a JSON string
* @param functions.importJson Function that imports data from a JSON string
* @param options Configuration options
* @param options.filePrefix Prefix for the downloaded filename
* @param options.onError Optional error handler called with a title and the error
* @param options.shouldExport Optional function to determine if export should proceed based on parsed data. Defaults to always true.
* @returns Object containing exportAsFile and importFromFile functions
*/
function createJsonFileIO({ exportJson, importJson }, { filePrefix, onError, shouldExport }) {
	const exportAsFile = async (ids) => {
		try {
			const jsonData = await exportJson(ids);
			const parsed = JSON.parse(jsonData);
			if (shouldExport && !shouldExport(parsed)) return;
			const blob = new Blob([jsonData], { type: "application/json" });
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `${filePrefix}-${(/* @__PURE__ */ new Date()).toISOString().split("T")[0]}.json`;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(url);
		} catch (error) {
			onError?.("Failed to export", error);
			throw error;
		}
	};
	const importFromFile = async (file) => {
		try {
			const text = await file.text();
			try {
				JSON.parse(text);
			} catch {
				throw new Error("Invalid JSON format");
			}
			return await importJson(text);
		} catch (error) {
			let errorMessage = "Failed to import";
			if (error instanceof Error) {
				if (error.message.includes("memory") || error.message.includes("quota")) errorMessage = "File too large - insufficient memory";
				else if (error.message.includes("Invalid JSON")) errorMessage = "Invalid JSON format";
			}
			onError?.(errorMessage, error);
			throw error;
		}
	};
	return {
		exportAsFile,
		importFromFile
	};
}

//#endregion
//#region src/react/createRepositoryHook.ts
/**
* Creates a comprehensive React hook that combines live queries, CRUD operations, file I/O,
* and custom repository methods into a single, easy-to-use interface.
*
* This is the main factory function for creating repository hooks in your application.
* It combines multiple lower-level hook factories to provide a complete data management solution.
*
* @template T The type of records in the repository
* @template Extra Additional methods provided by the repository beyond basic CRUD
* @param config Configuration object
* @param config.db The Dexie database instance
* @param config.tableName The name of the table to query
* @param config.query Optional custom query function for the live query hook
* @param config.repo The repository instance providing CRUD and custom methods
* @param config.onError Optional error handler for all operations
* @param config.fileIO Optional file I/O configuration for import/export
* @param config.expose Optional function to expose custom repository methods in the hook
* @returns A hook that provides records, loading state, CRUD operations, file I/O, and custom methods
*
* @example
* ```typescript
* const useMyData = createRepositoryHook({
*   db: myDatabase,
*   tableName: 'myTable',
*   repo: myRepository,
*   onError: (title, error) => toast.error(title),
*   fileIO: {
*     exportJson: (ids) => myRepository.export(ids),
*     importJson: (json) => myRepository.import(json),
*     filePrefix: 'my-data'
*   },
*   expose: (repo) => ({ customMethod: repo.customMethod })
* });
* ```
*/
function createRepositoryHook(config) {
	const useList = createLiveQueryHook(config.db, config.tableName, config.query);
	const useCrud = createCrudHook(config.repo, { onError: config.onError });
	const file = config.fileIO ? createJsonFileIO({
		exportJson: config.fileIO.exportJson,
		importJson: config.fileIO.importJson
	}, {
		filePrefix: config.fileIO.filePrefix,
		onError: config.onError,
		shouldExport: config.fileIO.shouldExport
	}) : void 0;
	const exposed = config.expose ? config.expose(config.repo) : {};
	return function useRepository() {
		const records = useList();
		const isLoading = records === void 0;
		const { save, update, remove, clear } = useCrud();
		return {
			records,
			isLoading,
			save,
			update,
			remove,
			clear,
			...file ? {
				exportAsFile: file.exportAsFile,
				importFromFile: file.importFromFile
			} : {},
			...exposed
		};
	};
}

//#endregion
//#region src/plugins/account-alias/types.ts
/**
* Default configuration options for AliasStorage.
* These values are used when options are not explicitly provided.
*/
const DEFAULT_OPTIONS = {
	duplicateMode: "strict",
	maxAliasLength: 64,
	tableName: "aliases",
	enableLogging: typeof process !== "undefined" ? process.env.NODE_ENV !== "production" : true,
	logLevel: "info"
};

//#endregion
//#region src/plugins/account-alias/errors.ts
/**
* Human-readable error messages for each error code.
*/
const ERROR_MESSAGES = {
	DUPLICATE_ALIAS: "An alias with this name already exists",
	ALIAS_TOO_LONG: "Alias name exceeds maximum allowed length",
	INVALID_ALIAS: "Alias name must be a non-empty string",
	INVALID_ADDRESS: "Address must be a non-empty string",
	ALIAS_NOT_FOUND: "No alias found with the specified ID",
	INVALID_IMPORT_FORMAT: "Import data is not in the expected format",
	STORAGE_QUOTA_EXCEEDED: "Storage quota exceeded"
};
/**
* Structured error for alias storage operations.
* Includes an error code and optional details for debugging.
*
* @example
* ```typescript
* try {
*   await aliasStorage.save({ address: '0x...', alias: 'Treasury' });
* } catch (err) {
*   if (err instanceof AliasStorageError && err.code === 'DUPLICATE_ALIAS') {
*     console.log('This alias name is already in use');
*   }
* }
* ```
*/
var AliasStorageError = class AliasStorageError extends Error {
	/**
	*
	*/
	constructor(code, details) {
		super(ERROR_MESSAGES[code]);
		this.name = "AliasStorageError";
		this.code = code;
		this.details = details;
		if (Error.captureStackTrace) Error.captureStackTrace(this, AliasStorageError);
	}
};
/**
* Type guard to check if an error is an AliasStorageError.
*
* @param err - The error to check
* @returns true if the error is an AliasStorageError
*/
function isAliasStorageError(err) {
	return err instanceof AliasStorageError;
}
/**
* Creates an AliasStorageError for duplicate alias detection.
*
* @param alias - The duplicate alias name
* @param existingAddress - The address that already has this alias
* @returns AliasStorageError with DUPLICATE_ALIAS code
*/
function duplicateAliasError(alias, existingAddress) {
	return new AliasStorageError("DUPLICATE_ALIAS", {
		alias,
		existingAddress
	});
}
/**
* Creates an AliasStorageError for alias length validation.
*
* @param alias - The alias that exceeded the limit
* @param maxLength - The configured maximum length
* @returns AliasStorageError with ALIAS_TOO_LONG code
*/
function aliasTooLongError(alias, maxLength) {
	return new AliasStorageError("ALIAS_TOO_LONG", {
		alias,
		length: alias.length,
		maxLength
	});
}
/**
* Creates an AliasStorageError for invalid alias.
*
* @returns AliasStorageError with INVALID_ALIAS code
*/
function invalidAliasError() {
	return new AliasStorageError("INVALID_ALIAS");
}
/**
* Creates an AliasStorageError for invalid address.
*
* @returns AliasStorageError with INVALID_ADDRESS code
*/
function invalidAddressError() {
	return new AliasStorageError("INVALID_ADDRESS");
}
/**
* Creates an AliasStorageError for alias not found.
*
* @param id - The ID that was not found
* @returns AliasStorageError with ALIAS_NOT_FOUND code
*/
function aliasNotFoundError(id) {
	return new AliasStorageError("ALIAS_NOT_FOUND", { id });
}
/**
* Creates an AliasStorageError for invalid import format.
*
* @param reason - Description of what's wrong with the format
* @returns AliasStorageError with INVALID_IMPORT_FORMAT code
*/
function invalidImportFormatError(reason) {
	return new AliasStorageError("INVALID_IMPORT_FORMAT", { reason });
}
/**
* Creates an AliasStorageError for storage quota exceeded.
*
* @param cause - The original quota error
* @returns AliasStorageError with STORAGE_QUOTA_EXCEEDED code
*/
function storageQuotaExceededError(cause) {
	return new AliasStorageError("STORAGE_QUOTA_EXCEEDED", { cause });
}

//#endregion
//#region src/plugins/account-alias/schema.ts
/**
* Default Dexie schema for the aliases table.
*
* Index explanation:
* - `++id`: Auto-increment primary key (overridden by `generateId()` for UUID)
* - `[address+networkId]`: Compound index for efficient lookup by (address, networkId) pair. Uniqueness is enforced at the application layer in `AliasStorage.save`.
* - `address`: Non-unique index for lookup by address across all networks
* - `networkId`: Non-unique index for lookup by network
* - `alias`: Non-unique index for lookup by alias name
* - `createdAt`, `updatedAt`: Indexed for chronological queries
*
* @example
* ```typescript
* import { ALIAS_SCHEMA, createDexieDatabase } from '@openzeppelin/ui-storage';
*
* const db = createDexieDatabase('MyApp', [{
*   version: 1,
*   stores: {
*     ...ALIAS_SCHEMA,
*     // ...other tables
*   }
* }]);
* ```
*/
const ALIAS_SCHEMA = { aliases: "++id, [address+networkId], address, networkId, alias, createdAt, updatedAt" };
/**
* Creates an alias schema with a custom table name.
* Use this when you need multiple independent alias stores in the same database.
*
* @param tableName - Custom table name for the aliases store
* @returns Schema object with the custom table name
*
* @example
* ```typescript
* const schema = createAliasSchema('myAliases');
* // { myAliases: '++id, [address+networkId], address, networkId, alias, createdAt, updatedAt' }
* ```
*/
function createAliasSchema(tableName = DEFAULT_OPTIONS.tableName) {
	return { [tableName]: "++id, [address+networkId], address, networkId, alias, createdAt, updatedAt" };
}
/**
* Gets the schema for alias storage based on provided options.
* Convenience function that respects the tableName option.
*
* @param options - Optional storage options containing tableName
* @returns Schema object for the configured table name
*/
function getAliasSchema(options) {
	return createAliasSchema(options?.tableName ?? DEFAULT_OPTIONS.tableName);
}

//#endregion
//#region src/plugins/account-alias/AliasStorage.ts
/**
* Sentinel value used to represent a global alias (no specific network) in IndexedDB.
* IndexedDB compound indexes don't support undefined/null, so we use this sentinel.
* This value is internal and converted to/from undefined in the public API.
*/
const GLOBAL_NETWORK_SENTINEL = "__global__";
/**
* Converts networkId for storage (undefined -> sentinel).
*/
function toStorageNetworkId(networkId) {
	return networkId === void 0 || networkId === "" ? GLOBAL_NETWORK_SENTINEL : networkId;
}
/**
* Converts networkId from storage (sentinel -> undefined).
*/
function fromStorageNetworkId(networkId) {
	return networkId === GLOBAL_NETWORK_SENTINEL ? void 0 : networkId;
}
/**
* Converts a record from storage format to API format.
*/
function fromStorageRecord(record) {
	return {
		...record,
		networkId: fromStorageNetworkId(record.networkId ?? GLOBAL_NETWORK_SENTINEL)
	};
}
/**
* AliasStorage provides address-to-alias mapping with configurable behavior.
*
* @example
* ```typescript
* import { createAliasStorage, createDexieDatabase, ALIAS_SCHEMA } from '@openzeppelin/ui-storage';
*
* const db = createDexieDatabase('MyApp', [{ version: 1, stores: ALIAS_SCHEMA }]);
* const aliasStorage = createAliasStorage(db, { duplicateMode: 'strict' });
*
* await aliasStorage.save({ address: '0x123...', alias: 'Treasury' });
* const record = await aliasStorage.getByAddress('0x123...');
* ```
*/
var AliasStorage = class {
	/**
	* Creates a new AliasStorage instance.
	*
	* @param db - Dexie database instance with alias schema
	* @param options - Configuration options
	*/
	constructor(db, options) {
		const hasMaxAliasLength = options !== void 0 && "maxAliasLength" in options;
		this.options = {
			...DEFAULT_OPTIONS,
			...options,
			maxAliasLength: hasMaxAliasLength ? options.maxAliasLength : DEFAULT_OPTIONS.maxAliasLength
		};
		this.table = db.table(this.options.tableName);
		this.log("debug", "Initialized with options", {
			duplicateMode: this.options.duplicateMode,
			maxAliasLength: this.options.maxAliasLength,
			tableName: this.options.tableName
		});
	}
	/**
	* Save a new alias or update existing alias for an address.
	* If the address already has an alias for the same networkId, it will be replaced.
	*
	* @param input - Alias data (address, alias, optional networkId and metadata)
	* @returns The ID of the created/updated record
	* @throws {AliasStorageError} DUPLICATE_ALIAS in strict mode when alias name exists for different address
	* @throws {AliasStorageError} ALIAS_TOO_LONG when exceeds maxAliasLength
	* @throws {AliasStorageError} INVALID_ALIAS when alias is empty
	* @throws {AliasStorageError} INVALID_ADDRESS when address is empty
	*/
	async save(input) {
		const apiNetworkId = input.networkId === "" ? void 0 : input.networkId;
		this.validateAddress(input.address);
		this.validateAlias(input.alias);
		const existing = await this.getByAddressAndNetwork(input.address, apiNetworkId);
		if (!existing) await this.checkDuplicate(input.alias);
		else if (existing.alias !== input.alias) await this.checkDuplicate(input.alias);
		return await this.withQuotaHandling(async () => {
			const now = /* @__PURE__ */ new Date();
			const storageNetworkId = toStorageNetworkId(apiNetworkId);
			if (existing) {
				await this.table.update(existing.id, {
					alias: input.alias,
					metadata: input.metadata,
					updatedAt: now
				});
				this.log("info", "Alias updated", {
					id: existing.id,
					alias: input.alias
				});
				return existing.id;
			}
			const id = generateId();
			const record = {
				id,
				address: input.address,
				networkId: storageNetworkId,
				alias: input.alias,
				metadata: input.metadata,
				createdAt: now,
				updatedAt: now
			};
			await this.table.add(record);
			this.log("info", "Alias saved", {
				id,
				address: input.address,
				alias: input.alias
			});
			return id;
		});
	}
	/**
	* Update an existing alias record.
	*
	* @param id - Record ID to update
	* @param updates - Partial updates to apply
	* @throws {AliasStorageError} ALIAS_NOT_FOUND when record doesn't exist
	* @throws {AliasStorageError} ALIAS_TOO_LONG when alias exceeds maxAliasLength
	* @throws {AliasStorageError} INVALID_ALIAS when alias is empty
	*/
	async update(id, updates) {
		if (updates.alias !== void 0) this.validateAlias(updates.alias);
		if (!await this.get(id)) throw aliasNotFoundError(id);
		return await this.withQuotaHandling(async () => {
			await this.table.update(id, {
				...updates,
				updatedAt: /* @__PURE__ */ new Date()
			});
			this.log("info", "Alias updated", {
				id,
				updates: Object.keys(updates)
			});
		});
	}
	/**
	* Get an alias record by ID.
	*
	* @param id - Record ID
	* @returns The record, or undefined if not found
	*/
	async get(id) {
		const record = await this.table.get(id);
		return record ? fromStorageRecord(record) : void 0;
	}
	/**
	* Get a global alias record by address (networkId undefined).
	*
	* @param address - Account address
	* @returns The global alias record, or undefined if address has no global alias
	*/
	async getByAddress(address) {
		return await this.getByAddressAndNetwork(address, void 0);
	}
	/**
	* Get an alias record by address and optional networkId.
	*
	* @param address - Account address
	* @param networkId - Network identifier matching NetworkConfig.id (undefined for global alias)
	* @returns The record, or undefined if (address, networkId) has no alias
	*/
	async getByAddressAndNetwork(address, networkId) {
		const storageNetworkId = toStorageNetworkId(networkId);
		const record = (await this.table.where("[address+networkId]").equals([address, storageNetworkId]).toArray())[0];
		return record ? fromStorageRecord(record) : void 0;
	}
	/**
	* Find all alias records for an address across all networks.
	*
	* @param address - Account address
	* @returns Array of all alias records for this address (may include global and network-specific)
	*/
	async findByAddress(address) {
		return (await this.table.where("address").equals(address).toArray()).map(fromStorageRecord);
	}
	/**
	* Get an alias record by alias name.
	* In 'allow' mode with duplicates, returns the first match.
	*
	* @param alias - Alias name
	* @returns The record, or undefined if alias doesn't exist
	*/
	async getByAlias(alias) {
		const record = await this.table.where("alias").equals(alias).first();
		return record ? fromStorageRecord(record) : void 0;
	}
	/**
	* Find all records with a given alias name.
	* Useful when duplicateMode is 'allow' or 'warn'.
	*
	* @param alias - Alias name to search
	* @returns Array of matching records
	*/
	async findByAlias(alias) {
		return (await this.table.where("alias").equals(alias).toArray()).map(fromStorageRecord);
	}
	/**
	* Resolve an alias name to an address.
	* Shorthand for getByAlias(alias)?.address.
	*
	* @param alias - Alias name
	* @returns The address, or undefined if alias doesn't exist
	*/
	async resolveAlias(alias) {
		return (await this.getByAlias(alias))?.address;
	}
	/**
	* Resolve an address to an alias name.
	* Shorthand for getByAddressAndNetwork(address, networkId)?.alias.
	*
	* @param address - Account address
	* @param networkId - Optional network identifier (undefined for global alias)
	* @returns The alias, or undefined if address has no alias for this network
	*/
	async resolveAddress(address, networkId) {
		return (await this.getByAddressAndNetwork(address, networkId))?.alias;
	}
	/**
	* Delete an alias by record ID.
	*
	* @param id - Record ID to delete
	*/
	async delete(id) {
		await this.table.delete(id);
		this.log("info", "Alias deleted", { id });
	}
	/**
	* Delete an alias by address.
	* No-op if address has no alias.
	*
	* @param address - Address to remove global alias for
	*/
	async deleteByAddress(address) {
		const record = await this.getByAddress(address);
		if (record) await this.delete(record.id);
	}
	/**
	* Delete an alias by alias name.
	* In 'allow' mode with duplicates, deletes all records with this alias.
	*
	* @param alias - Alias name to delete
	*/
	async deleteByAlias(alias) {
		const records = await this.findByAlias(alias);
		await this.table.bulkDelete(records.map((r) => r.id));
		if (records.length > 0) this.log("info", "Aliases deleted by name", {
			alias,
			count: records.length
		});
	}
	/**
	* Clear all aliases from storage.
	*/
	async clear() {
		await this.table.clear();
		this.log("info", "All aliases cleared");
	}
	/**
	* Get all alias records, ordered by updatedAt descending.
	*
	* @returns Array of all records
	*/
	async getAll() {
		return (await this.table.orderBy("updatedAt").reverse().toArray()).map(fromStorageRecord);
	}
	/**
	* Get alias records filtered by one or more network IDs, ordered by updatedAt descending.
	* Uses the indexed `networkId` column for efficient querying.
	*
	* @param networkIds - Network IDs to include
	* @returns Matching records sorted by updatedAt descending
	*/
	async getByNetworkIds(networkIds) {
		const storageIds = networkIds.map(toStorageNetworkId);
		return (await this.table.where("networkId").anyOf(storageIds).sortBy("updatedAt")).reverse().map(fromStorageRecord);
	}
	/**
	* Get the count of stored aliases.
	*
	* @returns Number of records
	*/
	async count() {
		return await this.table.count();
	}
	/**
	* Check if an address has an alias.
	*
	* @param address - Address to check
	* @param networkId - Optional network identifier (undefined checks global alias)
	* @returns true if alias exists for the (address, networkId) pair
	*/
	async hasAlias(address, networkId) {
		return await this.getByAddressAndNetwork(address, networkId) !== void 0;
	}
	/**
	* Check if an alias name is in use.
	*
	* @param alias - Alias name to check
	* @returns true if at least one record uses this alias
	*/
	async aliasExists(alias) {
		return await this.getByAlias(alias) !== void 0;
	}
	/**
	* Bulk save multiple aliases.
	*
	* @param inputs - Array of alias inputs
	* @returns Array of created/updated record IDs
	*/
	async bulkSave(inputs) {
		return this.table.db.transaction("rw", this.table, async () => {
			const ids = [];
			for (const input of inputs) {
				const id = await this.save(input);
				ids.push(id);
			}
			return ids;
		});
	}
	/**
	* Bulk delete records by IDs.
	*
	* @param ids - Array of record IDs to delete
	*/
	async bulkDelete(ids) {
		await this.table.bulkDelete(ids);
		this.log("info", "Bulk delete", { count: ids.length });
	}
	/**
	* Export aliases to JSON format.
	*
	* @param ids - Optional array of record IDs to export (all if omitted)
	* @returns JSON string of AliasExport
	*/
	async exportJson(ids) {
		let records;
		if (ids && ids.length > 0) records = (await Promise.all(ids.map((id) => this.get(id)))).filter((r) => r !== void 0);
		else records = await this.getAll();
		const exportData = {
			version: 1,
			exportedAt: (/* @__PURE__ */ new Date()).toISOString(),
			aliases: records.map((r) => ({
				address: r.address,
				networkId: r.networkId,
				alias: r.alias,
				metadata: r.metadata
			}))
		};
		return JSON.stringify(exportData, null, 2);
	}
	/**
	* Import aliases from JSON.
	*
	* @param json - JSON string in AliasExport format
	* @returns Import result with counts and IDs
	* @throws {AliasStorageError} INVALID_IMPORT_FORMAT when JSON is malformed or invalid schema
	*/
	async importJson(json) {
		let data;
		try {
			data = JSON.parse(json);
		} catch {
			throw invalidImportFormatError("Invalid JSON syntax");
		}
		this.validateImportSchema(data);
		const exportData = data;
		const ids = [];
		let skipped = 0;
		for (const alias of exportData.aliases) try {
			const id = await this.save({
				address: alias.address,
				networkId: alias.networkId,
				alias: alias.alias,
				metadata: alias.metadata
			});
			ids.push(id);
		} catch (err) {
			this.log("debug", "Skipped import entry", {
				address: alias.address,
				alias: alias.alias,
				error: err instanceof Error ? err.message : "Unknown error"
			});
			skipped++;
		}
		this.log("info", "Import completed", {
			imported: ids.length,
			skipped
		});
		return {
			imported: ids.length,
			skipped,
			ids
		};
	}
	/**
	* Validates the import data schema.
	*
	* @param data - Parsed JSON data to validate
	* @throws {AliasStorageError} INVALID_IMPORT_FORMAT when schema is invalid
	*/
	validateImportSchema(data) {
		if (typeof data !== "object" || data === null || Array.isArray(data)) throw invalidImportFormatError("Import data must be an object");
		const obj = data;
		if (typeof obj.version !== "number") throw invalidImportFormatError("Missing or invalid \"version\" field");
		if (obj.version !== 1) throw invalidImportFormatError(`Unsupported version: ${obj.version}. Only version 1 is supported`);
		if (!Array.isArray(obj.aliases)) throw invalidImportFormatError("Missing or invalid \"aliases\" array");
		for (let i = 0; i < obj.aliases.length; i++) {
			const entry = obj.aliases[i];
			if (typeof entry !== "object" || entry === null) throw invalidImportFormatError(`Invalid alias entry at index ${i}: must be an object`);
			const aliasEntry = entry;
			if (typeof aliasEntry.address !== "string" || aliasEntry.address.trim() === "") throw invalidImportFormatError(`Invalid alias entry at index ${i}: missing or invalid "address" field`);
			if (typeof aliasEntry.alias !== "string" || aliasEntry.alias.trim() === "") throw invalidImportFormatError(`Invalid alias entry at index ${i}: missing or invalid "alias" field`);
			if (aliasEntry.networkId !== void 0 && typeof aliasEntry.networkId !== "string") throw invalidImportFormatError(`Invalid alias entry at index ${i}: "networkId" must be a string`);
			if (aliasEntry.metadata !== void 0 && (typeof aliasEntry.metadata !== "object" || aliasEntry.metadata === null || Array.isArray(aliasEntry.metadata))) throw invalidImportFormatError(`Invalid alias entry at index ${i}: "metadata" must be an object`);
		}
	}
	/**
	* Validates that an alias is non-empty and within length limits.
	*
	* @param alias - The alias to validate
	* @throws {AliasStorageError} INVALID_ALIAS when alias is empty
	* @throws {AliasStorageError} ALIAS_TOO_LONG when alias exceeds maxAliasLength
	*/
	validateAlias(alias) {
		if (!alias || alias.trim() === "") throw invalidAliasError();
		if (this.options.maxAliasLength !== void 0 && alias.length > this.options.maxAliasLength) throw aliasTooLongError(alias, this.options.maxAliasLength);
	}
	/**
	* Validates that an address is non-empty.
	*
	* @param address - The address to validate
	* @throws {AliasStorageError} INVALID_ADDRESS when address is empty
	*/
	validateAddress(address) {
		if (!address || address.trim() === "") throw invalidAddressError();
	}
	/**
	* Checks for duplicate alias names based on duplicateMode configuration.
	*
	* @param alias - The alias to check for duplicates
	* @throws {AliasStorageError} DUPLICATE_ALIAS in strict mode when duplicate found
	*/
	async checkDuplicate(alias) {
		if (this.options.duplicateMode === "allow") return;
		const existing = await this.getByAlias(alias);
		if (!existing) return;
		if (this.options.duplicateMode === "strict") throw duplicateAliasError(alias, existing.address);
		if (this.options.onDuplicate) this.options.onDuplicate(alias, existing.address);
	}
	/**
	* Wraps an async operation with quota error handling.
	*
	* @param operation - The async operation to execute
	* @returns The result of the operation
	*/
	async withQuotaHandling(operation) {
		try {
			return await withQuotaHandling(this.options.tableName, operation);
		} catch (err) {
			if (err instanceof Error && err.message.includes("quota-exceeded")) throw storageQuotaExceededError(err);
			throw err;
		}
	}
	/**
	* Logs a message if logging is enabled.
	*
	* @param level - Log level
	* @param message - Log message
	* @param data - Optional data to log
	*/
	log(level, message, data) {
		if (!this.options.enableLogging) return;
		const logLevels = [
			"debug",
			"info",
			"warn",
			"error"
		];
		const configuredLevel = logLevels.indexOf(this.options.logLevel);
		if (logLevels.indexOf(level) >= configuredLevel) {
			const system = `AliasStorage:${this.options.tableName}`;
			if (data) logger[level](system, message, data);
			else logger[level](system, message);
		}
	}
};
/**
* Create an AliasStorage instance.
*
* @param db - Dexie database instance
* @param options - Configuration options
* @returns AliasStorage instance
*
* @example
* ```typescript
* const aliasStorage = createAliasStorage(db, {
*   duplicateMode: 'warn',
*   onDuplicate: (alias, addr) => console.warn(`Duplicate: ${alias}`),
* });
* ```
*/
function createAliasStorage(db, options) {
	return new AliasStorage(db, options);
}

//#endregion
//#region src/plugins/account-alias/react.ts
/**
* Creates a React hook for alias storage with live reactive updates.
*
* The returned hook provides:
* - Live reactive list of all aliases (auto-updates on changes)
* - CRUD operations with error handling
* - File import/export functionality
* - Convenience lookup methods
*
* This hook uses the shared createRepositoryHook pattern from the storage package
* to ensure consistency with other storage hooks.
*
* @param db - Dexie database instance with alias schema
* @param options - Configuration options including error handler
* @returns A React hook for alias storage
*
* @example
* ```typescript
* import { createDexieDatabase, ALIAS_SCHEMA } from '@openzeppelin/ui-storage';
* import { createUseAliasStorage } from '@openzeppelin/ui-storage/plugins/account-alias';
*
* const db = createDexieDatabase('MyApp', [{ version: 1, stores: ALIAS_SCHEMA }]);
*
* const useAliasStorage = createUseAliasStorage(db, {
*   duplicateMode: 'strict',
*   onError: (title, error) => toast.error(title),
* });
*
* function MyComponent() {
*   const { records, isLoading, save } = useAliasStorage();
*
*   if (isLoading) return <div>Loading...</div>;
*
*   return (
*     <ul>
*       {records?.map(r => <li key={r.id}>{r.alias}: {r.address}</li>)}
*     </ul>
*   );
* }
* ```
*/
function createUseAliasStorage(db, options) {
	const { onError, ...storageOptions } = options ?? {};
	const storage = createAliasStorage(db, storageOptions);
	const useBaseHook = createRepositoryHook({
		db,
		tableName: storageOptions.tableName ?? "aliases",
		query: (table) => table.orderBy("updatedAt").reverse().toArray(),
		repo: storage,
		onError,
		fileIO: {
			exportJson: (ids) => storage.exportJson(ids),
			importJson: async (json) => {
				return (await storage.importJson(json)).ids;
			},
			filePrefix: "aliases"
		},
		expose: (repo) => ({
			getByAddress: repo.getByAddress.bind(repo),
			getByAddressAndNetwork: repo.getByAddressAndNetwork.bind(repo),
			findByAddress: repo.findByAddress.bind(repo),
			getByAlias: repo.getByAlias.bind(repo),
			resolveAlias: repo.resolveAlias.bind(repo),
			resolveAddress: repo.resolveAddress.bind(repo)
		})
	});
	return function useAliasStorage() {
		const baseResult = useBaseHook();
		const wrap = useCallback(async (title, operation) => {
			try {
				return await operation();
			} catch (error) {
				onError?.(title, error);
				throw error;
			}
		}, []);
		const getByAddress = useCallback((address) => wrap("Failed to get alias by address", () => storage.getByAddress(address)), [wrap]);
		const getByAddressAndNetwork = useCallback((address, networkId) => wrap("Failed to get alias", () => storage.getByAddressAndNetwork(address, networkId)), [wrap]);
		const findByAddress = useCallback((address) => wrap("Failed to find aliases by address", () => storage.findByAddress(address)), [wrap]);
		const getByAlias = useCallback((alias) => wrap("Failed to get alias by name", () => storage.getByAlias(alias)), [wrap]);
		const resolveAlias = useCallback((alias) => wrap("Failed to resolve alias", () => storage.resolveAlias(alias)), [wrap]);
		const resolveAddress = useCallback((address, networkId) => wrap("Failed to resolve address", () => storage.resolveAddress(address, networkId)), [wrap]);
		return {
			records: baseResult.records,
			isLoading: baseResult.isLoading,
			save: baseResult.save,
			update: baseResult.update,
			remove: baseResult.remove,
			clear: baseResult.clear,
			exportAsFile: baseResult.exportAsFile,
			importFromFile: baseResult.importFromFile,
			getByAddress,
			getByAddressAndNetwork,
			findByAddress,
			getByAlias,
			resolveAlias,
			resolveAddress
		};
	};
}
/**
* Returns the underlying AliasStorage instance from the hook factory.
* Useful for advanced use cases where direct storage access is needed.
*
* @param db - Dexie database instance
* @param options - Storage options
* @returns AliasStorage instance
*/
function getAliasStorageInstance(db, options) {
	return createAliasStorage(db, options);
}

//#endregion
//#region src/plugins/account-alias/useAliasLabelResolver.ts
/**
* Creates a reactive `AddressLabelResolver` backed by alias storage.
*
* The returned object can be spread directly into `AddressLabelProvider`:
* ```tsx
* const resolver = useAliasLabelResolver(db);
* <AddressLabelProvider {...resolver}>...</AddressLabelProvider>
* ```
*
* @param db - Dexie database instance with alias schema
* @param options - Optional configuration including default networkId
* @returns An `AddressLabelResolver` with a synchronous `resolveLabel` function
*/
function useAliasLabelResolver(db, options) {
	const { networkId: defaultNetworkId, ...storageOptions } = options ?? {};
	const storage = useMemo(() => createAliasStorage(db, storageOptions), [
		db,
		storageOptions.tableName ?? "aliases",
		storageOptions.duplicateMode,
		storageOptions.maxAliasLength,
		storageOptions.enableLogging,
		storageOptions.logLevel
	]);
	const records = useLiveQuery(() => storage.getAll(), [storage]);
	return { resolveLabel: useCallback((address, networkId) => {
		if (!records) return void 0;
		const normalized = address.toLowerCase();
		const effectiveNetworkId = networkId ?? defaultNetworkId;
		if (effectiveNetworkId) {
			const networkMatch = records.find((r) => r.address.toLowerCase() === normalized && r.networkId === effectiveNetworkId);
			if (networkMatch) return networkMatch.alias;
		}
		return records.find((r) => r.address.toLowerCase() === normalized && !r.networkId)?.alias;
	}, [records, defaultNetworkId]) };
}

//#endregion
//#region src/plugins/account-alias/useAliasSuggestionResolver.ts
const MAX_SUGGESTIONS = 5;
/**
* Creates a reactive `AddressSuggestionResolver` backed by alias storage.
*
* The returned object can be spread directly into `AddressSuggestionProvider`:
* ```tsx
* const resolver = useAliasSuggestionResolver(db);
* <AddressSuggestionProvider {...resolver}>...</AddressSuggestionProvider>
* ```
*
* @param db - Dexie database instance with alias schema
* @param options - Optional configuration
* @returns An `AddressSuggestionResolver` with a synchronous `resolveSuggestions` function
*/
function useAliasSuggestionResolver(db, options) {
	const { maxSuggestions = MAX_SUGGESTIONS, ...storageOptions } = options ?? {};
	const storage = useMemo(() => createAliasStorage(db, storageOptions), [
		db,
		storageOptions.tableName ?? "aliases",
		storageOptions.duplicateMode,
		storageOptions.maxAliasLength,
		storageOptions.enableLogging,
		storageOptions.logLevel
	]);
	const records = useLiveQuery(() => storage.getAll(), [storage]);
	return { resolveSuggestions: useCallback((query, networkId) => {
		if (!records || !query.trim()) return [];
		const lower = query.toLowerCase();
		return records.filter((r) => {
			const matchesQuery = r.alias.toLowerCase().includes(lower) || r.address.toLowerCase().includes(lower);
			const matchesNetwork = !networkId || !r.networkId || r.networkId === networkId;
			return matchesQuery && matchesNetwork;
		}).sort((a, b) => a.alias.localeCompare(b.alias)).slice(0, maxSuggestions).map((r) => ({
			label: r.alias,
			value: r.address,
			description: r.networkId
		}));
	}, [records, maxSuggestions]) };
}

//#endregion
//#region src/plugins/account-alias/useAddressBookWidgetProps.ts
function toWidgetAlias(record) {
	return {
		id: record.id,
		address: record.address,
		alias: record.alias,
		networkId: record.networkId,
		createdAt: record.createdAt,
		updatedAt: record.updatedAt
	};
}
/**
* Bridge hook that returns spread-ready props for `AddressBookWidget`.
*
* Connects Dexie-backed alias storage to the renderer widget,
* providing reactive data, CRUD callbacks, and import/export support.
*/
function useAddressBookWidgetProps(db, options) {
	const { networkId, filterNetworkIds, onError, ...storageOptions } = options ?? {};
	const { duplicateMode, maxAliasLength, onDuplicate, enableLogging, logLevel } = storageOptions;
	const tableName = storageOptions.tableName ?? "aliases";
	const onDuplicateRef = useRef(onDuplicate);
	onDuplicateRef.current = onDuplicate;
	const stableOnDuplicate = useCallback((alias, existingAddress) => onDuplicateRef.current?.(alias, existingAddress), []);
	const hasOnDuplicate = !!onDuplicate;
	const storage = useMemo(() => createAliasStorage(db, {
		tableName,
		duplicateMode,
		maxAliasLength,
		onDuplicate: hasOnDuplicate ? stableOnDuplicate : void 0,
		enableLogging,
		logLevel
	}), [
		db,
		tableName,
		duplicateMode,
		maxAliasLength,
		enableLogging,
		logLevel,
		stableOnDuplicate,
		hasOnDuplicate
	]);
	const fileIO = useMemo(() => createJsonFileIO({
		exportJson: (ids) => storage.exportJson(ids),
		importJson: async (json) => {
			return (await storage.importJson(json)).ids;
		}
	}, {
		filePrefix: "aliases",
		onError
	}), [storage, onError]);
	const records = useLiveQuery(() => filterNetworkIds && filterNetworkIds.length > 0 ? storage.getByNetworkIds(filterNetworkIds) : storage.getAll(), [storage, filterNetworkIds?.slice().sort().join(",") ?? ""]);
	const aliases = useMemo(() => records?.map(toWidgetAlias), [records]);
	return {
		aliases,
		isLoading: aliases === void 0,
		onSave: useCallback(async (input) => {
			try {
				return await storage.save(input);
			} catch (error) {
				onError?.("Failed to save alias", error);
				throw error;
			}
		}, [storage, onError]),
		onRemove: useCallback(async (id) => {
			try {
				await storage.delete(id);
			} catch (error) {
				onError?.("Failed to remove alias", error);
				throw error;
			}
		}, [storage, onError]),
		onClear: useCallback(async () => {
			try {
				await storage.clear();
			} catch (error) {
				onError?.("Failed to clear aliases", error);
				throw error;
			}
		}, [storage, onError]),
		onExport: useCallback(async (ids) => {
			await fileIO.exportAsFile(ids);
		}, [fileIO]),
		onImport: useCallback(async (file) => {
			return await fileIO.importFromFile(file);
		}, [fileIO]),
		currentNetworkId: networkId
	};
}

//#endregion
//#region src/plugins/account-alias/useAliasEditCallbacks.ts
/**
* Creates Dexie-backed callbacks for `AliasEditPopover`.
*
* The returned object can be spread directly into the popover:
* ```tsx
* const editCallbacks = useAliasEditCallbacks(db);
* <AliasEditPopover {...editCallbacks} ... />
* ```
*
* @param db - Dexie database instance with alias schema
* @param options - Optional storage configuration
* @returns Callback props compatible with `AliasEditPopoverProps`
*/
function useAliasEditCallbacks(db, options) {
	const storage = useMemo(() => createAliasStorage(db, options), [
		db,
		options?.tableName ?? "aliases",
		options?.duplicateMode,
		options?.maxAliasLength,
		options?.enableLogging,
		options?.logLevel
	]);
	return {
		onLookup: useCallback(async (address, networkId) => {
			const record = await storage.getByAddressAndNetwork(address, networkId);
			if (!record) return void 0;
			return {
				id: record.id,
				alias: record.alias
			};
		}, [storage]),
		onSave: useCallback(async (input) => {
			return storage.save(input);
		}, [storage]),
		onRemove: useCallback(async (id) => {
			return storage.delete(id);
		}, [storage])
	};
}

//#endregion
export { ALIAS_SCHEMA, AliasStorage, AliasStorageError, DEFAULT_OPTIONS, ERROR_MESSAGES, EntityStorage, KeyValueStorage, aliasNotFoundError, aliasTooLongError, createAliasSchema, createAliasStorage, createCrudHook, createDexieDatabase, createJsonFileIO, createLiveQueryHook, createRepositoryHook, createUseAliasStorage, duplicateAliasError, getAliasSchema, getAliasStorageInstance, invalidAddressError, invalidAliasError, invalidImportFormatError, isAliasStorageError, isQuotaError, storageQuotaExceededError, useAddressBookWidgetProps, useAliasEditCallbacks, useAliasLabelResolver, useAliasSuggestionResolver, useLiveQuery, withQuotaHandling };
//# sourceMappingURL=index.mjs.map